import { useState } from "react";
import { CheckCircle, Circle, Plugs, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiError } from "../lib/api";

export default function IntegrationCard({ item, basePath, onChanged, allowedModes, dark = false }) {
  const [showConfig, setShowConfig] = useState(false);
  const [provider, setProvider] = useState(item.provider || "dishenda");
  const [creds, setCreds] = useState({});
  const [config, setConfig] = useState(item.config || {});
  const [testing, setTesting] = useState(false);

  const connected = item.status === "collegata";
  const modes = allowedModes?.[item.key] || ["dishenda", "ristorante"];
  const ristoranteAllowed = modes.includes("ristorante");
  const providerOptions = Object.entries(item.providers).filter(([k]) =>
    k === "dishenda" ? modes.includes("dishenda") : ristoranteAllowed);

  const cardCls = dark ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200";
  const titleCls = dark ? "text-white" : "text-zinc-900";
  const descCls = dark ? "text-zinc-400" : "text-zinc-500";
  const iconCls = dark
    ? (connected ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-500")
    : (connected ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-400");
  const offBadge = dark
    ? "bg-zinc-700/40 text-zinc-400 border-zinc-600"
    : "bg-zinc-100 text-zinc-500 border-zinc-300";
  const pillCls = dark ? "bg-zinc-800 border-zinc-700 text-zinc-300" : "bg-zinc-100 border-zinc-200 text-zinc-700";
  const primaryBtn = dark ? "bg-amber-400 text-zinc-950 hover:bg-amber-300" : "bg-zinc-900 text-white";
  const ghostBtn = dark ? "bg-zinc-800 border-zinc-700 text-zinc-200" : "bg-white border-zinc-300 text-zinc-700";
  const dangerBtn = dark ? "bg-zinc-800 border-rose-500/40 text-rose-300" : "bg-white border-rose-200 text-rose-600";

  const save = async (e) => {
    e.preventDefault();
    try {
      await (await import("../lib/api")).default.put(`${basePath}/integrations/${item.key}`, {
        provider, credentials: creds, config,
      });
      toast.success("Integrazione configurata");
      setShowConfig(false);
      setCreds({});
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const api = (await import("../lib/api")).default;
      const r = await api.post(`${basePath}/integrations/${item.key}/test`);
      if (r.data.ok) toast.success(r.data.message);
      else toast.error(r.data.message);
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
    finally { setTesting(false); }
  };

  const disconnect = async () => {
    try {
      const api = (await import("../lib/api")).default;
      await api.delete(`${basePath}/integrations/${item.key}`);
      toast.success("Integrazione disconnessa");
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  const inputCls = "w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
  const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";

  return (
    <div className={`${cardCls} border rounded-lg p-4`} data-testid={`integration-${item.key}`}>
      <div className="flex items-start gap-3">
        <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconCls}`}>
          <Plugs size={20} weight="bold" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-display font-bold ${titleCls}`}>{item.name}</span>
            {connected ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1" data-testid={`status-${item.key}`}>
                <CheckCircle size={10} weight="fill" /> COLLEGATA
              </span>
            ) : (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${offBadge}`} data-testid={`status-${item.key}`}>
                <Circle size={10} /> NON COLLEGATA
              </span>
            )}
          </div>
          <p className={`text-xs mt-1 ${descCls}`}>{item.description}</p>
          <div className={`flex items-center gap-2 mt-2 text-[11px] flex-wrap ${descCls}`}>
            {connected && (
              <>
                <span className={`border rounded-full px-2 py-0.5 font-medium ${pillCls}`}>
                  {item.managed_by === "dishenda" ? "Gestita da Dishenda" : `Provider del ristorante: ${item.providers[item.provider] || item.provider}`}
                </span>
                {item.last_verified_at && (
                  <span>Ultima verifica: {new Date(item.last_verified_at).toLocaleString("it-IT")}</span>
                )}
              </>
            )}
            {!connected && (
              <span className="text-zinc-400">Modalità consentite: {modes.includes("dishenda") ? "Dishenda" : ""}{modes.includes("dishenda") && ristoranteAllowed ? " · " : ""}{ristoranteAllowed ? "Ristorante" : ""}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-1.5 mt-3 flex-wrap">
        <button onClick={() => setShowConfig(true)} data-testid={`config-${item.key}`}
          className={`h-9 px-3 rounded-md text-xs font-bold ${primaryBtn}`}>
          {connected ? "Riconfigura" : "Configura"}
        </button>
        {connected && (
          <>
            <button onClick={test} disabled={testing} data-testid={`test-${item.key}`}
              className={`h-9 px-3 rounded-md border text-xs font-semibold disabled:opacity-50 ${ghostBtn}`}>
              {testing ? "Test..." : "Test connessione"}
            </button>
            <button onClick={disconnect} data-testid={`disconnect-${item.key}`}
              className={`h-9 px-3 rounded-md border text-xs font-semibold ${dangerBtn}`}>
              Disconnetti
            </button>
          </>
        )}
      </div>

      {showConfig && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowConfig(false)}>
          <form onSubmit={save} className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid={`config-form-${item.key}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Configura {item.name}</h3>
              <button type="button" onClick={() => setShowConfig(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div>
              <label className={labelCls}>Provider</label>
              <select value={provider} onChange={(e) => setProvider(e.target.value)} className={inputCls} data-testid={`provider-${item.key}`}>
                {providerOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              {!ristoranteAllowed && provider !== "dishenda" && (
                <p className="text-[11px] text-rose-600 mt-1">Provider del ristorante non consentito per questa integrazione.</p>
              )}
            </div>
            {provider !== "dishenda" && item.cred_fields.map((f) => (
              <div key={f.key}>
                <label className={labelCls}>{f.label}</label>
                <input type="password" value={creds[f.key] || ""} onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                  placeholder={item.credentials_masked?.[f.key] ? `Salvata: ${item.credentials_masked[f.key]}` : "••••••••"}
                  className={inputCls} data-testid={`cred-${item.key}-${f.key}`} autoComplete="off" />
              </div>
            ))}
            {provider !== "dishenda" && (
              <p className="text-[11px] text-zinc-400">Le credenziali sono salvate in modo sicuro sul backend e non vengono mai mostrate in chiaro. Lascia vuoto per mantenere i valori esistenti.</p>
            )}
            {item.config_fields.map((f) => (
              <div key={f.key}>
                <label className={labelCls}>{f.label}</label>
                {f.options ? (
                  <select value={config[f.key] || f.options[0]} onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                    className={inputCls} data-testid={`cfgf-${item.key}-${f.key}`}>
                    {f.options.map((o) => <option key={o} value={o}>{o.toUpperCase()}</option>)}
                  </select>
                ) : (
                  <input value={config[f.key] || ""} onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                    className={inputCls} data-testid={`cfgf-${item.key}-${f.key}`} />
                )}
              </div>
            ))}
            <button className="w-full h-11 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid={`save-${item.key}`}>Salva configurazione</button>
          </form>
        </div>
      )}
    </div>
  );
}
